// XP counter — extrinsic + balanced learners only.
// Intrinsic learners get null (the gate keeps points-chasing surfaces
// out of their experience to avoid the over-justification effect).
//
// In "full" variant, the counter doubles as a level badge — circular
// indigo ring with gold fill proportional to progress toward the next
// level (spec §11.5.10). Clicking opens a mini-dashboard popover.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronRight, Award } from 'lucide-react';
import { useMotivationalDriverVisibility } from '@shared/hooks/useMotivationalDriverVisibility';
import {
  subscribeGamificationSummary,
  ensureGamificationDoc,
} from '@shared/services/gamification';
import { type UserGamification, levelFromXp } from '@shared/types/gamification';
import { lookupAchievement } from '@shared/constants/points-config';

interface XPCounterProps {
  userId: string;
  /** "compact" hides the label and just shows the number; "full" shows the level pill + popover. */
  variant?: 'compact' | 'full';
}

export function XPCounter({ userId, variant = 'full' }: XPCounterProps) {
  const visibility = useMotivationalDriverVisibility(userId);
  const [summary, setSummary] = useState<UserGamification | null>(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visibility.intrinsic) return;
    let active = true;
    void ensureGamificationDoc(userId);
    const unsub = subscribeGamificationSummary(userId, (s) => {
      if (active) setSummary(s);
    });
    return () => {
      active = false;
      unsub();
    };
  }, [userId, visibility.intrinsic]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (visibility.intrinsic) return null;
  const xp = summary?.totalXp ?? 0;
  const level = levelFromXp(xp);

  if (variant === 'compact') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-body text-content-muted">
        <Sparkles size={12} className="text-gold-500" />
        {xp.toLocaleString()} XP
      </span>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Level ${level.current.level} ${level.current.title}, ${xp} XP`}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-pill border border-dark-border bg-dark-subtle hover:border-indigo-400/40 hover:bg-dark-hover transition-colors cursor-pointer"
      >
        <LevelRing level={level} />
        <span className="text-xs font-body text-content-muted uppercase tracking-wider hidden sm:inline">
          {level.current.title}
        </span>
        <span className="font-heading text-sm font-bold text-content">
          {xp.toLocaleString()}
        </span>
        <Sparkles size={12} className="text-gold-500" />
      </button>

      {open && summary && <MiniDashboard summary={summary} onClose={() => setOpen(false)} />}
    </div>
  );
}

function LevelRing({ level }: { level: ReturnType<typeof levelFromXp> }) {
  // 24px ring, gold-filled arc proportional to progress.
  const radius = 9;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - level.progress);
  return (
    <span className="relative inline-flex items-center justify-center" style={{ width: 24, height: 24 }}>
      <svg width={24} height={24} viewBox="0 0 24 24">
        <circle cx={12} cy={12} r={radius} fill="none" stroke="rgba(108, 92, 231, 0.35)" strokeWidth={2} />
        <circle
          cx={12}
          cy={12}
          r={radius}
          fill="none"
          stroke="#F0A500"
          strokeWidth={2}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 12 12)"
        />
      </svg>
      <span className="absolute font-heading text-[10px] font-bold text-content">
        {level.current.level}
      </span>
    </span>
  );
}

function MiniDashboard({ summary, onClose }: { summary: UserGamification; onClose: () => void }) {
  const level = levelFromXp(summary.totalXp);
  const recentAchievements = [...summary.achievements]
    .sort((a, b) => b.earnedAt.localeCompare(a.earnedAt))
    .slice(0, 3);

  return (
    <div
      role="dialog"
      className="absolute right-0 top-full mt-2 w-80 rounded-xl border border-dark-border bg-dark-surface shadow-modal overflow-hidden z-50"
    >
      <div className="px-4 py-3 border-b border-dark-border">
        <div className="text-xs font-body uppercase tracking-wider text-content-muted">Your Level</div>
        <div className="flex items-baseline justify-between mt-1">
          <div className="font-heading text-lg font-bold text-content">
            {level.current.level}. {level.current.title}
          </div>
          <div className="font-heading text-sm font-bold text-gold-500">
            {summary.totalXp.toLocaleString()} XP
          </div>
        </div>
        {level.next && (
          <>
            <div className="mt-2 h-1.5 rounded-full bg-dark-bg overflow-hidden">
              <div
                className="h-full bg-gold-500 rounded-full"
                style={{ width: `${level.progress * 100}%` }}
              />
            </div>
            <div className="text-xs font-body text-content-muted mt-1">
              {level.xpToNextLevel.toLocaleString()} XP to {level.next.title}
            </div>
          </>
        )}
        {!level.next && (
          <div className="text-xs font-body text-gold-500 mt-1">Max level reached.</div>
        )}
      </div>

      <div className="px-4 py-3 grid grid-cols-2 gap-3 border-b border-dark-border">
        <Stat label="Day streak" value={summary.currentStreakDays} />
        <Stat label="Longest" value={summary.longestStreakDays} />
        <Stat label="Achievements" value={summary.achievements.length} />
        <Stat label="Paths" value={summary.pathsCompleted} />
      </div>

      {recentAchievements.length > 0 && (
        <div className="px-4 py-3 border-b border-dark-border">
          <div className="text-xs font-body uppercase tracking-wider text-content-muted mb-2">
            Recent
          </div>
          <ul className="space-y-1.5">
            {recentAchievements.map((rec) => {
              const def = lookupAchievement(rec.id);
              return (
                <li key={rec.id} className="flex items-center gap-2 text-sm font-body text-content-secondary">
                  <Award size={14} className="text-gold-500 shrink-0" />
                  <span className="truncate">{def?.title ?? rec.id}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <Link
        to="/profile/progress"
        onClick={onClose}
        className="flex items-center justify-between px-4 py-3 hover:bg-dark-hover transition-colors"
      >
        <span className="text-sm font-body font-semibold text-content">View progress dashboard</span>
        <ChevronRight size={16} className="text-content-muted" />
      </Link>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs font-body text-content-muted">{label}</div>
      <div className="font-heading text-base font-bold text-content">{value.toLocaleString()}</div>
    </div>
  );
}
